import React, { useRef, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  useWindowDimensions,
  Animated,
} from "react-native";
import { useNavigate } from "react-router-dom";
import { Item } from "../../models/item_model";
import { primaryColor } from "../../services/theme_service";

interface CardListProps {
  title: string;
  items: Item[];
  onShowMore?: () => void;
  isMobileView?: boolean;
}

export const CardList: React.FC<CardListProps> = ({
  title,
  items,
  onShowMore,
  isMobileView = false,
}) => {
  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 500,
      useNativeDriver: true,
    }).start();
  }, []);

  // Split items into 2-column rows for mobile
  const rows = isMobileView
    ? items.reduce((acc: Item[][], curr, idx) => {
        if (idx % 2 === 0) acc.push([curr]);
        else acc[acc.length - 1].push(curr);
        return acc;
      }, [])
    : [];

  return (
    <Animated.View
      style={[
        {
          opacity: fadeAnim,
          transform: [
            {
              translateY: fadeAnim.interpolate({
                inputRange: [0, 1],
                outputRange: [20, 0],
              }),
            },
          ],
        },
        styles.celebrityContainer,
      ]}
    >
      <Text style={styles.celebrityTitle}>{title}</Text>

      {isMobileView ? (
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ paddingHorizontal: 10 }}
        >
          {rows.map((row, rowIndex) => (
            <View
              key={rowIndex}
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              {row.map((item) => (
                <Card
                  item={item}
                  key={item.item_id}
                  isMobileView={isMobileView}
                  totalPerRow={2}
                />
              ))}
              {row.length === 1 && <View style={{ flex: 1 }} />}
            </View>
          ))}
        </ScrollView>
      ) : (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ paddingLeft: 60 }}
        >
          {items.map((item) => (
            <Card item={item} key={item.item_id} isMobileView={false} />
          ))}
        </ScrollView>
      )}

      <View style={styles.showMoreContainer}>
        <TouchableOpacity style={styles.showMoreButton} onPress={onShowMore}>
          <Text style={styles.showMoreText}>Show More</Text>
        </TouchableOpacity>
      </View>
    </Animated.View>
  );
};

interface CardProps {
  item: Item;
  isMobileView?: boolean;
  totalPerRow?: number; 
}

export const Card: React.FC<CardProps> = ({
  item,
  isMobileView,
  totalPerRow = 2,
}) => {
  const navigate = useNavigate();
  const { width: screenWidth } = useWindowDimensions();

  const containerPadding = 10; // paddingHorizontal of ScrollView
  const cardSpacing = 10; // gap between cards

  const cardWidth = isMobileView
    ? (screenWidth - containerPadding * 2 - cardSpacing * (totalPerRow - 1)) / totalPerRow
    : 220;

  return (
    <TouchableOpacity
      onPress={() => navigate(`/details?id=${item.item_id}`)}
      key={item.item_id}
      style={[
        styles.celebrityCard,
        isMobileView && {
          width: cardWidth,
          marginRight: cardSpacing,
        },
      ]}
    >
      <Image
        source={{ uri: item.item_images[0]?.document_url }}
        style={[
          styles.celebrityImage,
          isMobileView && { height: cardWidth * 1.2 },
        ]}
        resizeMode="cover"
      />

      <View style={styles.celebrityCardContent}>
        <Text style={styles.celebrityProductName} numberOfLines={2}>
          {item.title}
        </Text>

        <View style={styles.celebrityRow}>
          <View style={styles.ratingContainer}>
            <Text style={styles.celebrityRating}>
              ⭐ {item.rating?.toFixed(1)}
            </Text>
          </View>
          <Text style={styles.celebrityModel}>{item.item_number}</Text>
        </View>

        <View style={styles.priceContainer}>
          <Text style={styles.celebrityPrice}>
            ₹ {item.final_price ?? ""}
          </Text>
        </View>
      </View>
    </TouchableOpacity>
  );
};

// --- styles remain the same ---


const styles = StyleSheet.create({
  celebrityContainer: {
    marginTop: 40,
    borderRadius: 20,
    backgroundColor: "#F8F1F1",
    paddingVertical: 40,
  },
  celebrityTitle: {
    fontSize: 26,
    fontWeight: "500",
    color: "#333",
    textAlign: "center",
    marginBottom: 30,
  },
  celebrityCard: {
    backgroundColor: "#fff",
    borderRadius: 15,
    marginRight: 12,
    width: 220,
    overflow: "hidden",
  },
  celebrityImage: {
    width: "100%",
    height: 300,
    borderTopLeftRadius: 15,
    borderTopRightRadius: 15,
  },
  celebrityCardContent: {
    padding: 10,
  },
  celebrityProductName: {
    fontSize: 16,
    fontWeight: "600",
    color: "#111",
    marginBottom: 4,
  },
  celebrityRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  celebrityRating: {
    color: "#777",
    fontSize: 14,
  },
  celebrityModel: {
    color: "#777",
    fontSize: 14,
    marginLeft: 15,
  },
  celebrityPrice: {
    fontSize: 18,
    fontWeight: "700",
    color: "#111",
  },
  ratingContainer: {
    backgroundColor: "#E8F4FF",
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  priceContainer: {
    alignSelf: "flex-start",
  },

  showMoreContainer: {
    marginTop: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  showMoreButton: {
    borderWidth: 1,
    borderColor: primaryColor,
    backgroundColor: "#fff",
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 24,
    marginTop: 20,
  },
  showMoreText: {
    color: primaryColor,
    fontWeight: "600",
    fontSize: 18,
  },
});
